import { Box, Grid, GridItem, Text, VStack, useColorModeValue } from '@chakra-ui/react';
import { format, startOfWeek, addDays, isSameDay } from 'date-fns';
import { CalendarActionItem } from '../useCalendarActionItems';
import { CalendarMilestone } from '../useCalendarMilestones';
import { isToday } from '../calendar.util';

interface CalendarWeekViewProps {
  currentDate: Date;
  actionItems: CalendarActionItem[];
  milestones: CalendarMilestone[];
}

/**
 * Calendar Week View Component
 * Displays a weekly grid with action items and milestones
 */
export function CalendarWeekView({
  currentDate,
  actionItems,
  milestones,
}: CalendarWeekViewProps) {
  const startDate = startOfWeek(currentDate, { weekStartsOn: 0 });
  const days = Array.from({ length: 7 }, (_, i) => addDays(startDate, i));

  const headerBg = useColorModeValue('gray.100', 'gray.700');
  const todayHeaderBg = useColorModeValue('blue.600', 'blue.500');
  const todayHeaderColor = 'white';
  const cellBg = useColorModeValue('white', 'gray.800');
  const borderColor = useColorModeValue('gray.200', 'gray.700');
  const actionItemBg = useColorModeValue('green.100', 'green.900');
  const actionItemBorder = useColorModeValue('green.600', 'green.400');
  const actionItemCompleteBg = useColorModeValue('gray.100', 'gray.700');
  const actionItemCompleteBorder = useColorModeValue('gray.400', 'gray.500');
  const milestoneBg = useColorModeValue('purple.100', 'purple.900');
  const milestoneBorder = useColorModeValue('purple.600', 'purple.400');
  const milestoneCompleteBg = useColorModeValue('gray.100', 'gray.700');
  const milestoneCompleteBorder = useColorModeValue('gray.400', 'gray.500');
  const textColor = useColorModeValue('gray.600', 'gray.400');
  const completeItemTextColor = useColorModeValue('gray.600', 'gray.400');

  const getItemsForDay = (day: Date) => {
    const dayActionItems = actionItems.filter((actionItem) => {
      if (!actionItem.dueDate) return false;
      const dueDate = actionItem.dueDate instanceof Date ? actionItem.dueDate : new Date(actionItem.dueDate);
      return isSameDay(dueDate, day);
    });

    const dayMilestones = milestones.filter((milestone) => {
      if (!milestone.dueDate) return false;
      const dueDate = milestone.dueDate instanceof Date ? milestone.dueDate : new Date(milestone.dueDate);
      return isSameDay(dueDate, day);
    });

    return { actionItems: dayActionItems, milestones: dayMilestones };
  };

  return (
    <Grid templateColumns="repeat(7, 1fr)" gap={0} borderWidth={1} borderColor={borderColor} borderRadius="md" overflow="hidden" minH="600px">
      {days.map((day, index) => {
        const { actionItems: dayActionItems, milestones: dayMilestones } = getItemsForDay(day);
        const isTodayDay = isToday(day);
        const allItems = [...dayActionItems, ...dayMilestones];

        return (
          <GridItem
            key={index}
            borderRightWidth={index < 6 ? 1 : 0}
            borderRightColor={borderColor}
            display="flex"
            flexDirection="column"
            h="600px"
            overflow="hidden"
          >
            {/* Day Header */}
            <Box
              p={2}
              borderBottomWidth={1}
              borderBottomColor={borderColor}
              bg={isTodayDay ? todayHeaderBg : headerBg}
              color={isTodayDay ? todayHeaderColor : undefined}
            >
              <Text fontWeight="semibold" fontSize="sm">
                {format(day, 'EEE')}
              </Text>
              <Text fontSize="2xl" fontWeight="bold">
                {format(day, 'd')}
              </Text>
            </Box>

            {/* Items List */}
            <Box p={2} flex={1} overflowY="auto" bg={cellBg}>
              <VStack spacing={2} align="stretch">
                {allItems.map((item, itemIndex) => {
                  const isActionItem = 'description' in item;
                  const isComplete = item.complete;
                  const bg = isComplete
                    ? isActionItem
                      ? actionItemCompleteBg
                      : milestoneCompleteBg
                    : isActionItem
                    ? actionItemBg
                    : milestoneBg;
                  const borderColor = isComplete
                    ? isActionItem
                      ? actionItemCompleteBorder
                      : milestoneCompleteBorder
                    : isActionItem
                    ? actionItemBorder
                    : milestoneBorder;
                  const text = isActionItem ? item.description : item.task;

                  return (
                    <Box
                      key={itemIndex}
                      p={2}
                      borderRadius="md"
                      bg={bg}
                      borderLeftWidth={2}
                      borderLeftColor={borderColor}
                      cursor="pointer"
                      _hover={{ opacity: 0.8 }}
                    >
                      <Text
                        fontWeight="medium"
                        fontSize="sm"
                        mb={1}
                        textDecoration={isComplete ? 'line-through' : 'none'}
                        color={isComplete ? completeItemTextColor : undefined}
                      >
                        {isActionItem ? '📋' : '🎯'} {text}
                      </Text>
                      {isActionItem && item.owner && (
                        <Text fontSize="xs" color={textColor} mt={1}>
                          {item.owner.name}
                        </Text>
                      )}
                      {!isActionItem && item.rock && (
                        <Text fontSize="xs" color={textColor} mt={1}>
                          {item.rock.rock}
                        </Text>
                      )}
                    </Box>
                  );
                })}
              </VStack>
            </Box>
          </GridItem>
        );
      })}
    </Grid>
  );
}

